import { type FC, type ReactNode } from "react";

import { clsx } from "clsx";

import { faLock } from "@fortawesome/pro-duotone-svg-icons/faLock";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Flat header chrome for an intake pane (the simple file-bucket types: Final
// Report, Dictation & notes, Supporting documents). The Records pane does NOT
// use this — it hosts ProcessRecordsCard / RecordsReviewSlot whole, with their
// own status/completed chrome.
export const IntakePane: FC<{
  title: string;
  subtitle: ReactNode;
  // High-consequence in a med-legal context: a failed report/record must not
  // read as "done". When `tone="error"` the subtitle carries the failed-state
  // CTA in rose, mirroring the treatment the old StageCard header had.
  tone?: "default" | "error";
  // Only the gating types (Final Report) carry a scope tag that mirrors the
  // composer's scope lock — `locked` tracks the same server gate, no second
  // signal.
  scope?: { label: string; locked: boolean };
  // Right-aligned header affordance (e.g. the "Add More" upload button that
  // used to live in the StageCard header controls).
  controls?: ReactNode;
  // Inside a panel that is already a card: no card of its own.
  flat?: boolean;
  children: ReactNode;
}> = ({
  title,
  subtitle,
  tone = "default",
  scope,
  controls,
  flat = false,
  children,
}) => (
  <div className={clsx(!flat && "surface-resting rounded-[18px] p-5")}>
    <div className="mb-1 flex items-center gap-2">
      <h3 className="text-ink text-[15px] font-semibold">{title}</h3>
      {scope && (
        <span
          className={clsx(
            "inline-flex items-center gap-[3px] rounded-full px-2 py-0.5 text-[10px] font-semibold",
            scope.locked
              ? "bg-slate-100 text-slate-500"
              : "bg-emerald-100 text-emerald-700",
          )}
        >
          {scope.label}
          {scope.locked && (
            <FontAwesomeIcon icon={faLock} className="text-[9px]" />
          )}
        </span>
      )}
      {controls && (
        <div className="ml-auto flex shrink-0 gap-2">{controls}</div>
      )}
    </div>
    <p
      className={clsx(
        "mb-4 text-xs",
        tone === "error" ? "font-medium text-rose-500" : "text-slate-500",
      )}
    >
      {subtitle}
    </p>
    {children}
  </div>
);
